import { Component, Input, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { EdlIconService, IconName } from './edl-icon.service';

export type IconNameType = IconName | string;

@Component({
    selector: 'edl-icon',
    template: `
        <span
            class="edl-icon"
            [style.width.px]="sizeInPixels"
            [style.height.px]="sizeInPixels"
            [style.color]="color"
            [attr.role]="ariaHidden ? null : 'img'"
            [attr.aria-label]="ariaHidden ? null : ariaLabel"
            [attr.aria-hidden]="ariaHidden"
            [innerHTML]="svgContent"
        ></span>
    `,
    styles: [`
        :host {
            display: inline-flex;
            align-items: center;
            justify-content: center;
        }
        .edl-icon {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
        }
        .edl-icon ::ng-deep svg {
            width: 100%;
            height: 100%;
            fill: currentColor;
        }
    `]
})
export class EdlIconComponent implements OnInit, OnChanges {
    @Input() name!: IconNameType;
    @Input() size: 'sm' | 'md' | 'lg' | 'xl' | number = 'md';
    @Input() color?: string;
    @Input() ariaLabel?: string;
    @Input() ariaHidden = false;

    svgContent: SafeHtml = '';
    sizeInPixels = 16;

    constructor(
        private iconService: EdlIconService,
        private sanitizer: DomSanitizer
    ) {}

    ngOnInit(): void {
        this.calculateSize();
        this.loadIcon();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['size']) {
            this.calculateSize();
        }
        if (changes['name']) {
            this.loadIcon();
        }
    }

    private calculateSize(): void {
        if (typeof this.size === 'number') {
            this.sizeInPixels = this.size;
        } else {
            const sizeMap = {
                sm: 16,
                md: 24,
                lg: 32,
                xl: 48
            };
            this.sizeInPixels = sizeMap[this.size] || 24;
        }
    }

    private loadIcon(): void {
        const svg = this.iconService.getIcon(this.name);
        if (svg) {
            // Safe to bypass sanitization here because all SVG content is hardcoded
            // at compile time in EdlIconService.registerIcons(). The `name` input is
            // only used as a registry key — unknown names return undefined and nothing
            // is rendered. If SVGs are ever sourced from HTTP responses or user input,
            // this bypass must be removed and Angular's default sanitization used instead.
            this.svgContent = this.sanitizer.bypassSecurityTrustHtml(svg);
        } else {
            console.warn(`EdlIconComponent: Icon "${this.name}" not found in registry`);
        }
    }
}
